import React, { useState, useEffect, useContext } from "react";
import { Routes, Route, BrowserRouter, Navigate, useLocation } from "react-router-dom";
import { ChakraProvider } from "@chakra-ui/react";
import { UserProvider, UserContext } from "./components/UserContext";
import Papa from "papaparse";
import ClientBriefForm from "./components/ClientBriefForm";
import ClientConnectionPage from "./components/client/ClientConnectionPage";
import PluginAuthorizationPage from "./components/client/PluginAuthorizationPage";
import PlanPage from "./components/PlanPage";
import Register from "./components/Register";
import Login from "./components/Login";
import Authorization from "./components/Authorization";
import ResetPassword from "./components/RessetPassword";
import UpdatePassword from "./components/UpdatePassword";
import ThankYou from "./components/ThankYou";
import ProtectedRoute from "./components/ProtectedRoute";
import HomepageNew from "./components/homepage";
import PricingPage from "./components/homepage/pages/Pricing";
import SimpleHomepageDemo from "./components/homepage/pages/SimpleHomepageDemo";
import AboutPage from "./components/homepage/pages/About";
import ContactPage from "./components/homepage/pages/Contact";
import PrivacyPage from "./components/homepage/pages/Privacy";
import TermsPage from "./components/homepage/pages/Terms";
import EULAPage from "./components/homepage/pages/EULA";
import Profile from "./components/Profile";
import Account from "./components/Account";
import DataPrivacy from "./components/DataPrivacy";
import PlanUsage from "./components/PlanUsage";
import ConnectorsPage, { KnowledgeSourcesPage, AiToolsPage } from "./components/ConnectorsPage";
import Brand from "./components/Brand";
import SavedPlansPanel from "./components/SavedPlansPanel";
import theme from "./components/theme";
import TestConnection from "./components/TestConnection";
import EndpointTester from "./components/EndpointTester";
import AdminCoreProducts from './components/AdminCoreProducts';
import AdminFeatures from './components/AdminFeatures';
import AdminWorkflows from './components/AdminWorkflows';
import AdminImplementation from './components/AdminImplementation';
import AdminAgents from './components/AdminAgents';
import AdminAICapabilities from './components/AdminAICapabilities';
import {
  AdminLayout,
  AdminHome,
  AdminUsers,
  AdminPasscodes,
  AdminSystem,
  AdminPilotTracker,
  AdminTokenUsage,
  AdminCreditManagement,
  AdminAgentActivity,
} from './components/Admin';
import LowCreditBanner from './components/LowCreditBanner';
import AdminPlanSettings from './components/Admin/AdminPlanSettings';
import TestEmail from "./components/Test-email";
import Unauthorized from './components/Unauthorized';
import FormatTestComponent from "./components/FormatTestComponent";
import SharedPlanViewer from "./components/SharedPlanViewer";
import DocsPage from "./components/docs/DocsPage";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import UpgradePage from "./components/UpgradePage";
import Billing from "./components/Billing";
import BriefCardsProto from "./components/proto/BriefCardsProto";
import PabsBrainLab from "./components/labs/PabsBrainLab";
import ServiceUnavailableOverlay from "./components/ServiceUnavailableOverlay";
import AuthComplete from './components/auth/AuthComplete';

// Inner component to access UserContext
function AppRoutes({ programsOfWork, methods, tools, deliverables, briefsExamples, token }) {
  const { setIsChatCollapsed } = useContext(UserContext);
  const location = useLocation();

  // Reset chat collapsed state when navigating away from plan pages
  useEffect(() => {
    if (!location.pathname.startsWith('/plan')) {
      setIsChatCollapsed(true);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <LowCreditBanner />
      <Routes>
        <Route path="/client/connect/:id" element={<ProtectedRoute allowAnonymous><ClientConnectionPage /></ProtectedRoute>} />
        <Route path="/client/plugin-connect" element={<ProtectedRoute allowAnonymous><PluginAuthorizationPage /></ProtectedRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/complete" element={<AuthComplete />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/share/:token" element={<SharedPlanViewer />} />
        <Route path="/authorization" element={<Authorization />} />
        {/* One Terms document: older links land on /terms */}
        <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
        <Route path="/testEmail" element={<TestEmail />} />
        <Route
          path="/"
          element={token ? <Navigate to="/brief" replace /> : <SimpleHomepageDemo />}
        />
        <Route path="/platform" element={<HomepageNew />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/eula" element={<EULAPage />} />
        <Route
          path="/brief"
          element={
            <ProtectedRoute>
              <ClientBriefForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/c/:conversationId"
          element={
            <ProtectedRoute>
              <ClientBriefForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plan"
          element={
            <ProtectedRoute>
              {location.state?.initiateStrategyCreation || location.state?._cacheKey ? (
                <PlanPage
                  programsOfWork={programsOfWork}
                  methods={methods}
                  tools={tools}
                  deliverables={deliverables}
                  briefsExamples={briefsExamples}
                />
              ) : (
                <Navigate to="/brief" replace />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/plan/:planId"
          element={
            <ProtectedRoute>
              <PlanPage
                programsOfWork={programsOfWork}
                methods={methods}
                tools={tools}
                deliverables={deliverables}
                briefsExamples={briefsExamples}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route
          path="/data-privacy"
          element={
            <ProtectedRoute>
              <DataPrivacy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plan-usage"
          element={
            <ProtectedRoute>
              <PlanUsage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/upgrade"
          element={
            <ProtectedRoute>
              <UpgradePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connectors"
          element={
            <ProtectedRoute>
              <ConnectorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connectors/knowledge"
          element={
            <ProtectedRoute>
              <KnowledgeSourcesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/connectors/ai-tools"
          element={
            <ProtectedRoute>
              <AiToolsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/brand"
          element={
            <ProtectedRoute>
              <Brand />
            </ProtectedRoute>
          }
        />
        <Route path="/test-connection" element={<TestConnection />} />
        <Route path="/endpoint-tester" element={<EndpointTester />} />
        <Route path="/admin-core-products" element={<ProtectedRoute adminOnly={true}><AdminCoreProducts /></ProtectedRoute>} />
        <Route path="/admin-features" element={<ProtectedRoute adminOnly={true}><AdminFeatures /></ProtectedRoute>} />
        <Route path="/admin-workflows" element={<ProtectedRoute adminOnly={true}><AdminWorkflows /></ProtectedRoute>} />
        <Route path="/admin-implementation" element={<ProtectedRoute adminOnly={true}><AdminImplementation /></ProtectedRoute>} />
        <Route 
          path="/admin-agents" 
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminAgents />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin-ai-capabilities" 
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminAICapabilities />
            </ProtectedRoute>
          } 
        />
        <Route path="/unauthorized" element={<Unauthorized />} />
        
        {/* New Admin Section */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminHome />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="passcodes" element={<Navigate to="/admin/system/passcodes" replace />} />
          <Route path="plans" element={<Navigate to="/admin/system/plans" replace />} />
          <Route path="agents" element={<Navigate to="/admin/system/agents" replace />} />
          <Route path="capabilities" element={<Navigate to="/admin/system/capabilities" replace />} />
          <Route path="pilot" element={<AdminPilotTracker />} />
          <Route path="credits" element={<AdminCreditManagement />} />
          <Route path="agent-activity" element={<AdminAgentActivity />} />
          <Route path="token-usage" element={<AdminTokenUsage />} />
          <Route path="system" element={<AdminSystem />} />
          <Route path="system/products" element={<AdminCoreProducts standalone={true} />} />
          <Route path="system/features" element={<AdminFeatures standalone={true} />} />
          <Route path="system/passcodes" element={<AdminPasscodes />} />
          <Route path="system/plans" element={<AdminPlanSettings />} />
          <Route path="system/workflows" element={<AdminWorkflows standalone={true} />} />
          <Route path="system/agents" element={<AdminAgents standalone={true} />} />
          <Route path="system/capabilities" element={<AdminAICapabilities standalone={true} />} />
          <Route path="system/implementation" element={<AdminImplementation standalone={true} />} />
        </Route>
        
        {/* Prototypes — auth required, not linked from anywhere */}
        <Route
          path="/proto/brief-cards"
          element={
            <ProtectedRoute>
              <BriefCardsProto />
            </ProtectedRoute>
          }
        />
        <Route
          path="/labs/brain"
          element={
            <ProtectedRoute>
              <PabsBrainLab />
            </ProtectedRoute>
          }
        />

        {/* Public docs, no authentication required */}
        <Route path="/docs" element={<Navigate to="/docs/create-your-first-plan" replace />} />
        <Route path="/docs/:slug" element={<DocsPage />} />

        <Route path="*" element={<Navigate to="/brief" replace />} />
        <Route
          path="/format-test"
          element={
            <ProtectedRoute>
              <FormatTestComponent />
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Render SavedPlansPanel once at app level, outside of routes */}
      {/* Hide on admin routes since admin has its own layout */}
      {(() => {
        const hasToken = localStorage.getItem('token');
        const currentPath = window.location.pathname;
        const isAdminRoute = currentPath.startsWith('/admin');
        const isAuthRoute = currentPath === '/login' || currentPath === '/signup' || currentPath === '/forgot-password';
        const isDocsRoute = currentPath.startsWith('/docs');
        const isShareRoute = currentPath.startsWith('/share');
        const isUpgradeRoute = currentPath.startsWith('/upgrade') || currentPath.startsWith('/subscription');
        const shouldRender = !!hasToken && !isAdminRoute && !isAuthRoute && !isDocsRoute && !isShareRoute && !isUpgradeRoute && !currentPath.startsWith('/client/');
        return shouldRender ? (
          <SavedPlansPanel />
        ) : null;
      })()}
    </>
  );
}

function App() {
  const [programsOfWork, setProgramsOfWork] = useState([]);
  const [methods, setMethods] = useState([]);
  const [tools, setTools] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [briefsExamples, setBriefsExamples] = useState([]);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [serviceGeneration, setServiceGeneration] = useState(0);

  // Once the readiness screen confirms recovery, remount the data-owning app
  // tree so one-shot loaders run again instead of leaving stale error states.
  useEffect(() => {
    const handleServiceRestored = () => setServiceGeneration(value => value + 1);
    window.addEventListener('pabs:service-restored', handleServiceRestored);
    return () => window.removeEventListener('pabs:service-restored', handleServiceRestored);
  }, []);

  // Removed client-side www->apex redirect: Render's own custom-domain redirect
  // (apex -> www) now handles canonical-domain routing server-side. Keeping both
  // in place caused an infinite redirect loop (Render sends apex->www, this then
  // sent www->apex, forever) that took the whole site down.

  // Listen for token changes (e.g., after login)
  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem("token");
      setToken(newToken);
    };

    // Listen for localStorage changes
    window.addEventListener("storage", handleStorageChange);
    
    // Also check periodically in case storage event doesn't fire
    const tokenCheckInterval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (currentToken !== token) {
        setToken(currentToken);
      }
    }, 10000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(tokenCheckInterval);
    };
  }, [token]);

  useEffect(() => {
    const loadCsv = (url, setState) => {
      Papa.parse(url, {
        download: true,
        header: true,
        complete: (result) => setState(result.data),
        error: (err) => console.error("Error loading CSV:", err),
      });
    };

    loadCsv("/database/programs_of_work.csv", setProgramsOfWork);
    loadCsv("/database/methods.csv", setMethods);
    loadCsv("/database/tools.csv", (data) => {
      const toolsWithIds = data.map((tool, index) => ({
        ...tool,
        id: tool.id || `tool-${index}`,
        duration: parseFloat(tool.duration) || 1,
      }));
      setTools(toolsWithIds);
    });
    loadCsv("/database/deliverables.csv", setDeliverables);
    loadCsv("/database/briefs_examples.csv", setBriefsExamples);
  }, []);

  /*useEffect(() => {
    // Check if the script already exists to avoid duplicates
    if (window.location.pathname === '/brief') {
        const script = document.createElement('script');
        script.src = 'https://sak.userreport.com/pablo/launcher.js';
        script.async = true;
        script.id = 'userreport-launcher-script';
        document.body.appendChild(script);

        return () => {
            // Clean up by removing the script when the component unmounts
            document.body.removeChild(script);
        };
    }
}, []);*/

  return (
    <ChakraProvider theme={theme}>
      <ServiceUnavailableOverlay />
      <UserProvider key={serviceGeneration}>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <AppRoutes 
            programsOfWork={programsOfWork}
            methods={methods}
            tools={tools}
            deliverables={deliverables}
            briefsExamples={briefsExamples}
            token={token}
          />
        </BrowserRouter>
      </UserProvider>
    </ChakraProvider>
  );
}

export default App;
